'use client';

import { useCallback, useEffect, useState } from 'react';

import { detectInstallPlatform, isStandalone, type InstallPlatform } from '@/lib/pwa/install-platform';

/* `beforeinstallprompt` is Chromium-only and, critically, fires *once* and
   early , often before any React tree that cares about it has mounted. Missing
   it means falling back to written instructions on a device that could have
   offered a single-tap system dialog, so it is captured at module scope the
   moment this module is imported, and replayed to whoever asks later. */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let installedThisSession = false;
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((fn) => fn());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Suppress Chrome's own mini-infobar so our modal owns the moment.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installedThisSession = true;
    deferredPrompt = null;
    notify();
  });
}

export function useAddToHomeScreen() {
  const [platform, setPlatform] = useState<InstallPlatform>({ kind: 'unsupported' });
  const [installed, setInstalled] = useState(false);

  const sync = useCallback(() => {
    setInstalled(installedThisSession || isStandalone());
    setPlatform(detectInstallPlatform(Boolean(deferredPrompt)));
  }, []);

  useEffect(() => {
    sync();
    listeners.add(sync);
    const displayMode = window.matchMedia?.('(display-mode: standalone)');
    displayMode?.addEventListener('change', sync);
    window.addEventListener('pageshow', sync);
    return () => {
      listeners.delete(sync);
      displayMode?.removeEventListener('change', sync);
      window.removeEventListener('pageshow', sync);
    };
  }, [sync]);

  /** Fire the real system install dialog. Chromium only; resolves to the
   *  user's choice, or null when no native prompt was available. */
  const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | null> => {
    if (!deferredPrompt) return null;
    // Consume before awaiting so two mounted install entries cannot reuse it.
    const prompt = deferredPrompt;
    deferredPrompt = null;
    notify();
    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      return outcome;
    } catch {
      return null;
    }
  }, []);

  return { platform, installed, promptInstall };
}
